import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
} from "react";
import { message } from "antd";
import moment from "moment";
import {
    fetchWarehouseStock,
    fetchActiveTotalsAsync,
    updateOrderPurchaseStatus,
    generateSuppliesLeadsFile,
    fetchPurchasePeriodStatuses,
    createPurchasePeriodStatus,
    addWarehouseIncoming,
    setWarehouseStockDirect,
    updateWarehouseMinMax,
    deleteWarehouseStockEntry,
    fetchConsumptionForecast,
} from "../utils/fetch";

const SuppliesContext = createContext(null);

export const useSupplies = () => {
    const ctx = useContext(SuppliesContext);
    if (!ctx) throw new Error("useSupplies must be used within SuppliesProvider");
    return ctx;
};

// ─── Константы ──────────────────────────────────────────────────────────────

const STATUS_MAP = {
    NOT_PURCHASED: "Незакуплено",
    PURCHASED: "Закуплено",
    SHIPPED: "Отгружено",
};

const COLOR_MAP = {
    BLACK: "чёрный",
    SILVER: "серебро",
    GOLD: "золото",
    CHAMPAGNE: "шампань",
    WITHOUT_ALUMINIUM: "без алюминия",
    Черный: "чёрный",
    Чёрный: "чёрный",
    Серебро: "серебро",
    Шампань: "шампань",
    Золото: "золото",
    "Без короба": "без алюминия",
    "Без алюминия": "без алюминия",
};

const PREDEFINED_ORDER = [
    "короб 57 чёрный", "короб 57 серебро", "короб 57 шампань",
    "короб 42 чёрный", "короб 42 серебро", "короб 42 шампань",
    "торец 57 чёрный", "торец 57 серебро", "торец 57 шампань",
    "торец 42 наружный чёрный", "торец 42 внутренний чёрный", "торец 42 наружный серебро",
    "торец 42 внутренний серебро", "торец 42 наружный шампань", "торец 42 внутренний шампань",
    "каркас алюминиевый анодированный", "скрытый плинтус алюминиевый чёрный",
    "скрытый плинтус алюминиевый серебро", "скрытый плинтус алюминиевый шампань",
    "уплотнитель чёрный", "уплотнитель серебро", "уплотнитель шампань",
    "сухари алюминиевый под 57", "сухари алюминиевый под 42",
    "петли скрытые 3.2 чёрный", "петли скрытые 3.2 серебро", "петли скрытые 3.2 шампань",
    "петли скрытые 2.2 чёрный", "петли скрытые 2.2 серебро", "петли скрытые 2.2 шампань",
    "петли скрытые с доводчиком чёрный", "петли скрытые с доводчиком шампань", "петли скрытые с доводчиком серебро",
    "магнитный механизм под сан.уз чёрный", "магнитный механизм под сан.уз серебро", "магнитный механизм под сан.уз шампань",
    "магнитный механизм под ключ чёрный", "магнитный механизм под ключ серебро", "магнитный механизм под ключ шампань",
    "магнитный механизм touch чёрный", "магнитный механизм touch серебро",
    "вертушка под сан. узел чёрный", "вертушка под сан. узел серебро", "вертушка под сан. узел шампань",
    "вертушка под ключ/ключ чёрный", "вертушка под ключ/ключ серебро", "вертушка под ключ/ключ шампань",
    "вертушка под ключ/защелка чёрный", "вертушка под ключ/защелка серебро", "вертушка под ключ/защелка шампань",
    "раздвижной механизм с флажком", "раздвижной механизм пенал с флажком", "раздвижной механизм magic",
    "доводчик стандартный чёрный", "доводчик стандартный серебро", "доводчик стандартный шампань",
    "стопор скрытый чёрный", "стопор скрытые серебро", "стопор скрытый шампань",
    "стопор клеющийся чёрный", "стопор клеющийся серебро", "стопор клеющийся шампань",
    "скрытый порог 600", "скрытый порог 700", "скрытый порог 800", "скрытый порог 900",
    "дверные ручки space чёрный", "дверные ручки space серебро", "дверные ручки space шампань",
    "дверные ручки wave чёрный", "дверные ручки wave серебро", "дверные ручки wave шампань",
    "скрытые ручки invisible", "дверной шпингалет чёрный", "дверной шпингалет серебро", "дверной шпингалет шампань",
    "вентиляционные решетки",
];

// ─── Утилиты ────────────────────────────────────────────────────────────────

/**
 * Нормализация для LOOKUP (сравнение ключей).
 * Всегда lowercase, единообразные символы.
 * Используется И при записи, И при чтении — гарантирует совпадение ключей.
 */
function normalizeMaterialKey(name) {
    if (!name) return "";
    return name
        .trim()
        .toLowerCase()
        .replace(/анодированый/g, "анодированный")
        .replace(/черный/g, "чёрный")
        .replace(/\s+/g, " ");
}

/**
 * Вычислить ключ материала из аксессуара заказа.
 * Единая точка формирования ключа.
 */
function getAccessoryMaterialKey(acc) {
    const base = acc.color
        ? `${acc.name} ${(COLOR_MAP[acc.color] || acc.color).toLowerCase()}`
        : acc.name;
    return normalizeMaterialKey(base);
}

/**
 * Генерация периодов (недели текущего месяца)
 */
function getMonthPeriods() {
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");
    const periods = [];
    let current = startOfMonth.clone();
    let index = 0;

    while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, "day")) {
        const periodEnd = current.clone().add(6, "days").isAfter(endOfMonth)
            ? endOfMonth.clone()
            : current.clone().add(6, "days");

        periods.push({
            key: `p${index}`,
            label: `${current.format("DD/MM")} – ${periodEnd.format("DD/MM")}`,
            dateFrom: current.clone(),
            dateTo: periodEnd.clone(),
        });

        current = periodEnd.clone().add(1, "day");
        index++;
    }

    return periods;
}

/**
 * Определить, в какой период попадает дата
 */
function getDatePeriodKey(date, periods) {
    if (!date || !periods.length) return null;
    const m = moment(date);
    for (const period of periods) {
        if (
            (m.isSame(period.dateFrom, "day") || m.isAfter(period.dateFrom, "day")) &&
            (m.isSame(period.dateTo, "day") || m.isBefore(period.dateTo, "day"))
        ) {
            return period.key;
        }
    }
    return null;
}

/**
 * Получить позицию сортировки из PREDEFINED_ORDER.
 * Используется normalizeMaterialKey для гарантии совпадения.
 */
function getSortIndex(name) {
    const normalized = normalizeMaterialKey(name);
    const idx = PREDEFINED_ORDER.indexOf(normalized);
    return idx === -1 ? 999999 : idx;
}

/**
 * Преобразовать данные Application с бэкенда в формат фронта
 */
export function mapOrderFromApi(app) {
    const agreementNum = app.lead?.agreementNumber || app.docNumber || "";
    const clientName = app.client?.[0]?.name || app.client?.[0]?.companyName || "";
    const clientId = app.client?.[0]?._id || app.lead?.client || "";

    const orderYear = app.createdAt
        ? new Date(app.createdAt).getFullYear().toString().slice(-2)
        : "26";

    const aluminiumStr = Array.isArray(app.aluminium)
        ? [...new Set(app.aluminium.filter(a => a && typeof a === "string" && a.trim() !== ""))].join(", ")
        : app.aluminium || "";

    return {
        key: app._id,
        _id: app._id,
        clientId,
        num: 0,
        contractNum: `${orderYear}-${agreementNum}`,
        deadline: app.deadline
            ? new Date(app.deadline).toLocaleDateString("ru-RU", {
                day: "numeric",
                month: "numeric",
                year: "2-digit",
            })
            : "",
        deadlineDate: app.deadline,
        priority: mapPriority(app.status),
        status: STATUS_MAP[app.accessoriesPurchaseStatus] || "",
        backendStatus: app.accessoriesPurchaseStatus || "NOT_PURCHASED",
        aluminium: aluminiumStr,
        doorCount: app.doorCount || 0,
        totalPrice: app.totalPrice || 0,
        isHasMontage: app.isHasMontage || false,
        clientName,
        aluminum: aluminiumStr || "",
        loops: "",
        mechanism: "",
        doorHandles: "",
        extraFittings: "",
        other: "",
    };
}

function mapPriority(appStatus) {
    switch (appStatus) {
        case "BOOKING":
            return "Бронь";
        case "REVIEW":
        case "PRODUCTION":
            return "Запуск";
        default:
            return "Стоп";
    }
}

// ─── Провайдер ──────────────────────────────────────────────────────────────

export const SuppliesProvider = ({ children }) => {
    const [rawOrders, setRawOrders] = useState([]);
    const [warehouseStock, setWarehouseStock] = useState([]);
    const [totalAccessories, setTotalAccessories] = useState([]);
    const [orderAccessoriesMap, setOrderAccessoriesMap] = useState({});
    const [periodStatuses, setPeriodStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Исторический расход за 90 дней (из WarehouseMovement OUTGOING)
    const [consumptionHistory, setConsumptionHistory] = useState([]);

    const periods = useMemo(() => getMonthPeriods(), []);

    // ─── Загрузка данных ────────────────────────────────────────────────────

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [totalsResult, stock, statuses, consumptionData] = await Promise.all([
                fetchActiveTotalsAsync(),
                fetchWarehouseStock(),
                fetchPurchasePeriodStatuses(),
                fetchConsumptionForecast(),
            ]);

            setPeriodStatuses(statuses || []);
            setWarehouseStock(stock);
            setTotalAccessories(totalsResult?.totals || []);
            setConsumptionHistory(consumptionData || []);

            const orders = totalsResult?.periodData || [];
            setRawOrders(orders);

            const accMap = {};
            orders.forEach(app => {
                accMap[app._id] = app.accessories || [];
            });
            setOrderAccessoriesMap(accMap);
        } catch (e) {
            console.error("Ошибка загрузки данных снабжения:", e);
            setError("Ошибка загрузки данных. Попробуйте обновить страницу.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ─── Действия ───────────────────────────────────────────────────────────

    const updateOrderStatus = useCallback(async (orderId, newStatusBackend, newStatusRu) => {
        try {
            const result = await updateOrderPurchaseStatus([orderId], newStatusBackend);

            if (result?.stock) {
                setWarehouseStock(result.stock);
            }

            await loadData();
            message.success(`Статус изменён на «${newStatusRu}»`);
            return true;
        } catch (e) {
            console.error("Ошибка при обновлении статуса:", e);
            message.error("Не удалось обновить статус");
            return false;
        }
    }, [loadData]);

    const addIncoming = useCallback(async (materialName, quantity, date, comment) => {
        try {
            const result = await addWarehouseIncoming(materialName, quantity, date, comment);

            if (result?.stock) {
                setWarehouseStock(result.stock);
            }

            await loadData();
            message.success(`«${materialName}» — ${quantity} ед. добавлено на склад`);
            return result;
        } catch (e) {
            console.error("Ошибка прихода на склад:", e);
            message.error("Не удалось добавить товар на склад");
            throw e;
        }
    }, [loadData]);

    const setStockDirect = useCallback(async (materialName, newInStock) => {
        try {
            const result = await setWarehouseStockDirect(materialName, newInStock);

            if (result?.stock) {
                setWarehouseStock(result.stock);
            }

            await loadData();
            return result;
        } catch (e) {
            console.error("Ошибка корректировки склада:", e);
            throw e;
        }
    }, [loadData]);

    const updateMinMaxDays = useCallback(async (materialName, minStock, maxStock, deliveryDays) => {
        try {
            const result = await updateWarehouseMinMax(materialName, minStock, maxStock, deliveryDays);

            if (result) {
                setWarehouseStock(prev =>
                    prev.map(item =>
                        item.materialName === materialName
                            ? {
                                ...item,
                                minStock: result.minStock,
                                maxStock: result.maxStock,
                                deliveryDays: result.deliveryDays,
                            }
                            : item
                    )
                );
                await loadData();
            }
            return result;
        } catch (e) {
            console.error("Ошибка обновления настроек:", e);
            throw e;
        }
    }, [loadData]);

    const deleteStockEntry = useCallback(async (id) => {
        try {
            const result = await deleteWarehouseStockEntry(id);

            if (result.success) {
                setWarehouseStock(prev => prev.filter(item => item._id !== id));
                await loadData();
            }
            return result;
        } catch (e) {
            console.error("Ошибка удаления:", e);
            throw e;
        }
    }, [loadData]);

    // ─── Генерация PDF ──────────────────────────────────────────────────────

    const handleGeneratePdf = useCallback(
        (itemsOrIds, period) => {
            if (!Array.isArray(itemsOrIds) || itemsOrIds.length === 0) {
                console.error("Неверный формат на генерацию PDF", itemsOrIds);
                message.error("В этом периоде нет файлов для генерации");
                return;
            }

            const isItemObjects = itemsOrIds.every(
                item => typeof item === "object" && item !== null && "name" in item && "quantity" in item
            );

            if (period && period.dateFrom && period.dateTo) {
                const relevantOrderIds = rawOrders
                    .filter(app => {
                        if (!app.deadline) return false;
                        const d = moment(app.deadline);
                        return (
                            (d.isSame(period.dateFrom, "day") || d.isAfter(period.dateFrom, "day")) &&
                            (d.isSame(period.dateTo, "day") || d.isBefore(period.dateTo, "day"))
                        );
                    })
                    .map(app => app._id);

                if (relevantOrderIds.length === 0) {
                    message.warning("Нет заявок в выбранном периоде");
                    return;
                }

                const targetIds = isItemObjects ? relevantOrderIds : itemsOrIds;
                generateSuppliesLeadsFile(targetIds, () => { });

                createPurchasePeriodStatus(period.key, period.dateFrom, period.dateTo, itemsOrIds)
                    .then(newStatus => {
                        setPeriodStatuses(prev => [...prev, newStatus]);
                    })
                    .catch(err => console.error("Ошибка сохранения статуса периода", err));
            } else {
                generateSuppliesLeadsFile(itemsOrIds, () => { });
            }
        },
        [rawOrders]
    );

    // ─── Plan data ──────────────────────────────────────────────────────────

    const planData = useMemo(() => {
        if (!totalAccessories || totalAccessories.length === 0) return [];

        // Маппинг normalizedKey → оригинальное складское имя (для UI)
        const warehouseDisplayNames = new Map();

        // ── Шаг 1: Инициализация материалов из складских данных ──────────────
        // Ключ в allMaterials — ВСЕГДА normalizeMaterialKey (lowercase + замены)
        const allMaterials = new Map();

        warehouseStock.forEach(s => {
            const key = normalizeMaterialKey(s.materialName);
            warehouseDisplayNames.set(key, s.materialName);

            allMaterials.set(key, {
                _id: s._id,
                displayName: s.materialName,
                normalizedKey: key,
                unit: s.unit || "Штук",
                inStock: s.inStock || 0,
                minStock: s.minStock || 0,
                maxStock: s.maxStock || 0,
                needed: 0,
                neededThisMonth: 0,
                orderedNotReceived: 0,
                deliveryDays: s.deliveryDays || 2,
                category: s.category || "IN_BUILT",
            });
        });

        // ── Шаг 2: Проход по заказам — накопление needed + periodDistribution ──
        const periodDistribution = {};

        rawOrders.forEach(app => {
            const accessories = orderAccessoriesMap[app._id] || [];
            const isPurchased = app.status === "PURCHASED";

            accessories.forEach(acc => {
                const matKey = getAccessoryMaterialKey(acc);
                const count = Math.round(acc.count || 0);
                if (count <= 0) return;

                // Создать запись, если материала ещё нет
                if (!allMaterials.has(matKey)) {
                    const rawName = acc.color
                        ? `${acc.name} ${(COLOR_MAP[acc.color] || acc.color).toLowerCase()}`
                        : acc.name;

                    allMaterials.set(matKey, {
                        _id: matKey,
                        displayName: warehouseDisplayNames.get(matKey) || rawName,
                        normalizedKey: matKey,
                        unit: acc.unit || "Штук",
                        inStock: 0,
                        minStock: 0,
                        maxStock: 0,
                        needed: 0,
                        neededThisMonth: 0,
                        orderedNotReceived: 0,
                        deliveryDays: 2,
                        category: "IN_BUILT",
                    });
                }

                const mat = allMaterials.get(matKey);

                // Общая потребность (все заказы, независимо от дедлайна)
                mat.needed += count;

                // Уже закуплено, ждём доставку
                if (isPurchased) {
                    mat.orderedNotReceived += count;
                }

                // Распределение по периодам (только текущий месяц)
                if (app.deadline) {
                    const deliveryDays = mat.deliveryDays || 2;
                    const orderDate = moment(app.deadline).subtract(deliveryDays, "days");

                    let periodKey = getDatePeriodKey(orderDate, periods);

                    // Просроченные — в первую неделю
                    if (!periodKey && orderDate.isBefore(periods[0]?.dateFrom)) {
                        periodKey = periods[0]?.key;
                    }

                    if (periodKey) {
                        if (!periodDistribution[matKey]) {
                            periodDistribution[matKey] = {};
                        }
                        periodDistribution[matKey][periodKey] =
                            (periodDistribution[matKey][periodKey] || 0) + count;

                        mat.neededThisMonth += count;
                    }
                }
            });
        });

        // ── Шаг 3: Дополнение из totalAccessories (isFallback) ──────────────
        totalAccessories.forEach(acc => {
            const key = getAccessoryMaterialKey(acc);

            if (allMaterials.has(key)) {
                if (acc.isFallback) {
                    allMaterials.get(key).isFallback = true;
                }
            } else {
                const rawName = acc.color
                    ? `${acc.name} ${(COLOR_MAP[acc.color] || acc.color).toLowerCase()}`
                    : acc.name;

                allMaterials.set(key, {
                    _id: key,
                    displayName: warehouseDisplayNames.get(key) || rawName,
                    normalizedKey: key,
                    unit: acc.unit || "Штук",
                    inStock: 0,
                    minStock: 0,
                    maxStock: 0,
                    needed: Math.round(acc.count || 0),
                    neededThisMonth: 0,
                    orderedNotReceived: 0,
                    deliveryDays: 2,
                    category: "IN_BUILT",
                    isFallback: acc.isFallback || false,
                });
            }
        });

        // ── Шаг 4: Сортировка ───────────────────────────────────────────────
        const sortedMaterials = Array.from(allMaterials.values()).sort((a, b) => {
            const iA = getSortIndex(a.displayName);
            const iB = getSortIndex(b.displayName);

            if (iA !== iB) return iA - iB;

            if (a.category === b.category) {
                return a.displayName.localeCompare(b.displayName, "ru");
            }
            return a.category === "IN_BUILT" ? -1 : 1;
        });

        // ── Шаг 5: Формирование результата ──────────────────────────────────
        return sortedMaterials.map((item, index) => {
            const neededTotal = item.needed || 0;
            const inStock = item.inStock || 0;
            const ordered = item.orderedNotReceived || 0;
            const requiredToBuy = Math.max(0, neededTotal - inStock - ordered);

            // Чтение по normalizedKey — тот же ключ, что использовался при записи
            const materialPeriods = periodDistribution[item.normalizedKey] || {};
            const periodData = {};
            periods.forEach(p => {
                periodData[p.key] = materialPeriods[p.key] || 0;
            });

            return {
                key: item._id,
                num: index + 1,
                name: item.displayName,
                normalizedName: item.normalizedKey,
                unit: item.unit,
                min: item.minStock || 0,
                max: item.maxStock || 0,
                needed: neededTotal,
                neededThisMonth: item.neededThisMonth || 0,
                orderedNotReceived: ordered,
                inStock,
                toOrder: requiredToBuy,
                deliveryDays: item.deliveryDays || 2,
                category: item.category,
                isFallback: item.isFallback || false,
                documents: [],
                ...periodData,
            };
        });
    }, [warehouseStock, totalAccessories, rawOrders, orderAccessoriesMap, periods]);

    // ─── Прогноз снабжения ───────────────────────────────────────────────────
    // Рассчитывается на основе реального расхода за 90 дней (WarehouseMovement OUTGOING).
    // Если данных нет — используется fallback по активным заявкам (planData.needed).
    // В поле source: 'historical' | 'estimated' — явно указывается источник данных.

    const supplyForecastData = useMemo(() => {
        if (!warehouseStock || warehouseStock.length === 0) return [];

        // Строим map: normalizedName → { totalOutgoing, movementsCount, lastMovementDate }
        const historyMap = new Map();
        if (Array.isArray(consumptionHistory)) {
            consumptionHistory.forEach(item => {
                const key = normalizeMaterialKey(item.materialName);
                historyMap.set(key, {
                    totalOutgoing: item.totalOutgoing || 0,
                    movementsCount: item.movementsCount || 0,
                    lastMovementDate: item.lastMovementDate || null,
                });
            });
        }

        // Строим map из planData для «в пути» и «потребности»: normalizedName → planItem
        const planMap = new Map();
        planData.forEach(p => {
            planMap.set(normalizeMaterialKey(p.name), p);
        });

        const DAYS = 90;
        const rows = [];

        warehouseStock.forEach(stockItem => {
            const normalizedKey = normalizeMaterialKey(stockItem.materialName);
            const inStock = stockItem.inStock || 0;
            const deliveryDays = stockItem.deliveryDays || 2;
            const planItem = planMap.get(normalizedKey);
            // «В пути» — закуплены, но ещё не пришли (orderedNotReceived из planData)
            const inTransit = planItem?.orderedNotReceived || 0;
            const activeNeeded = planItem?.needed || 0;
            // Потребность именно в этом месяце (только заявки с дедлайном в текущем месяце)
            const neededThisMonth = planItem?.neededThisMonth || 0;

            // ── Источник данных ──
            const histData = historyMap.get(normalizedKey);
            const totalOutgoing90 = histData?.totalOutgoing || 0;
            const movementsCount = histData?.movementsCount || 0;
            const lastMovementDate = histData?.lastMovementDate || null;

            // ── Определяем суточный расход ──
            // Приоритет: реальная история → текущий месяц → все заявки
            let source;
            let dailyUsage; // суток. потребление

            if (totalOutgoing90 > 0) {
                // Реальный исторический расход: берём среднее за 90 дней
                source = 'historical';
                dailyUsage = totalOutgoing90 / DAYS;
            } else if (neededThisMonth > 0) {
                // Fallback 1: потребность текущего месяца / 30 дней
                // Это реалистичная оценка — только заявки с дедлайном в текущем месяце
                source = 'estimated';
                dailyUsage = neededThisMonth / 30;
            } else if (activeNeeded > 0) {
                // Fallback 2: общая потребность по всем заявкам / 90 дней
                // НЕ умножаем на 3 — activeNeeded уже является суммарным запросом
                source = 'estimated';
                dailyUsage = activeNeeded / 90;
            } else {
                // Нет ни расхода, ни потребности
                source = 'no_data';
                dailyUsage = 0;
            }

            // consumption90 — для отображения в таблице (сколько уходит за 90 дней)
            const consumption90 = parseFloat((dailyUsage * DAYS).toFixed(2));

            // ── Формулы по ТЗ ──
            // Дней хватит (только по складу)
            const daysLeft = dailyUsage > 0
                ? Math.floor(inStock / dailyUsage)
                : null;

            // Дней хватит (с учётом «в пути»)
            const daysLeftWithTransit = dailyUsage > 0
                ? Math.floor((inStock + inTransit) / dailyUsage)
                : null;

            // Потребность на срок поставки = Расход/день * deliveryDays
            const neededForDelivery = dailyUsage * deliveryDays;

            // К заказу = max(0, Потребность на поставку - На складе - В пути)
            const toOrder = Math.max(0, Math.ceil(neededForDelivery - inStock - inTransit));

            // ── Статус риска ──
            // По ТЗ:
            //   🔴 Критично: дней хватит (только склад) < deliveryDays И с учётом в пути тоже не хватит
            //   🟠 Скоро: склад не покроет срок, но «в пути» спасает → нельзя ждать до последнего
            //   🟢 В норме: inStock + inTransit >= neededForDelivery (ТЗ-условие)
            let riskStatus;
            if (dailyUsage === 0) {
                riskStatus = 'no_data';
            } else if (daysLeft !== null && daysLeft < deliveryDays && (daysLeftWithTransit === null || daysLeftWithTransit < deliveryDays)) {
                // Критично: даже с «в пути» не доживём — надо срочно заказывать
                riskStatus = 'critical';
            } else if (daysLeft !== null && daysLeft < deliveryDays) {
                // Склад не покрывает, но «в пути» спасает ситуацию — предупреждение
                riskStatus = 'warning';
            } else if (daysLeft !== null && daysLeft < deliveryDays * 1.5) {
                // Приближаемся к границе — скоро нужно заказывать
                riskStatus = 'warning';
            } else {
                // В норме: inStock + inTransit >= neededForDelivery
                riskStatus = 'ok';
            }

            // ── Фильтр: показываем только если есть что-то интересное ──
            const hasStock = inStock > 0;
            const hasNeed = activeNeeded > 0 || toOrder > 0;
            const isLongDelivery = deliveryDays >= 7;
            const hasHistory = totalOutgoing90 > 0;

            if (!hasStock && !hasNeed && !hasHistory && !isLongDelivery) return;

            rows.push({
                key: stockItem._id || normalizedKey,
                materialName: stockItem.materialName,
                unit: stockItem.unit || 'шт.',
                inStock,
                deliveryDays,
                inTransit,
                activeNeeded,
                neededThisMonth,
                // Расход
                consumption90: Math.round(consumption90),
                dailyUsage: parseFloat(dailyUsage.toFixed(3)),
                // Прогноз
                daysLeft,
                daysLeftWithTransit,
                neededForDelivery: Math.ceil(neededForDelivery),
                toOrder,
                // Мета
                source,          // 'historical' | 'estimated' | 'no_data'
                riskStatus,      // 'critical' | 'warning' | 'ok' | 'no_data'
                movementsCount,
                lastMovementDate,
            });

        });

        // Сортировка: critical → warning → ok → no_data, затем по toOrder DESC
        const riskOrder = { critical: 0, warning: 1, ok: 2, no_data: 3 };
        rows.sort((a, b) => {
            const rA = riskOrder[a.riskStatus] ?? 4;
            const rB = riskOrder[b.riskStatus] ?? 4;
            if (rA !== rB) return rA - rB;
            return b.toOrder - a.toOrder;
        });

        return rows;
    }, [warehouseStock, consumptionHistory, planData]);

    // ─── Значение контекста ─────────────────────────────────────────────────

    const value = useMemo(
        () => ({
            rawOrders,
            planData,
            supplyForecastData,
            warehouseStock,
            periods,
            periodStatuses,
            loading,
            error,
            updateOrderStatus,
            handleGeneratePdf,
            addIncoming,
            setStockDirect,
            updateMinMaxDays,
            deleteStockEntry,
            reload: loadData,
        }),
        [
            rawOrders,
            planData,
            supplyForecastData,
            periods,
            periodStatuses,
            warehouseStock,
            loading,
            error,
            updateOrderStatus,
            handleGeneratePdf,
            addIncoming,
            setStockDirect,
            updateMinMaxDays,
            deleteStockEntry,
            loadData,
        ]
    );

    return (
        <SuppliesContext.Provider value={value}>
            {children}
        </SuppliesContext.Provider>
    );
};

export default SuppliesContext;